import { db, functions } from '../firebase'
import {
  collection,
  addDoc,
  updateDoc,
  getDocs,
  deleteDoc,
  doc,
  getDoc,
  query,
  orderBy,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import {
  addPayment,
  getPaymentByInvoiceId,
  deletePayment,
} from './paymentService'

// Invoice data shape stored in Firestore
export interface InvoiceData {
  studentId: string
  studentName: string
  amount: number
  date: string
  status: 'Paid' | 'Pending'
  billingType?: 'Monthly' | 'Per Lesson'
  billingPeriod?: string
  lessonDate?: string
  notes?: string
  sent?: boolean
  sentAt?: any
}

// Invoice as returned from Firestore (with id + timestamps)
export interface Invoice extends InvoiceData {
  id: string
  createdAt?: any
  updatedAt?: any
}

// Helper: remove undefined values before writing to Firestore
const removeUndefinedValues = (
  obj: Record<string, any>,
): Record<string, any> => {
  return Object.fromEntries(
    Object.entries(obj).filter(([_, value]) => value !== undefined),
  )
}

/**
 * Fetch all invoices for a teacher, ordered by date descending.
 */
export async function getTeacherInvoices(
  teacherId: string,
): Promise<Invoice[]> {
  const invoicesRef = collection(db, 'teachers', teacherId, 'invoices')
  const snapshot = await getDocs(invoicesRef)

  const invoices = snapshot.docs.map((doc) => ({
    id: doc.id,
    ...(doc.data() as InvoiceData),
    createdAt: doc.data().createdAt,
    updatedAt: doc.data().updatedAt,
  }))

  // Sort client-side by date descending (avoids needing a Firestore index)
  return invoices.sort((a, b) => (b.date || '').localeCompare(a.date || ''))
}

/**
 * Create a new invoice in Firestore.
 */
export async function createInvoice(
  teacherId: string,
  invoiceData: InvoiceData,
): Promise<string> {
  const invoicesRef = collection(db, 'teachers', teacherId, 'invoices')

  const dataToWrite = removeUndefinedValues({
    ...invoiceData,
    amount: Number(invoiceData.amount),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })

  const docRef = await addDoc(invoicesRef, dataToWrite)
  return docRef.id
}

/**
 * Update an existing invoice in Firestore.
 * When status changes to Paid, auto-creates a linked payment record.
 * When status reverts to Pending, soft-deletes the linked payment.
 */
export async function updateInvoice(
  teacherId: string,
  invoiceId: string,
  updates: Partial<InvoiceData>,
): Promise<void> {
  const invoiceRef = doc(db, 'teachers', teacherId, 'invoices', invoiceId)

  const dataToWrite = removeUndefinedValues({
    ...updates,
    ...(updates.amount !== undefined ? { amount: Number(updates.amount) } : {}),
    updatedAt: serverTimestamp(),
  })

  await updateDoc(invoiceRef, dataToWrite)

  // --- Invoice → Revenue sync ---
  try {
    if (updates.status === 'Paid') {
      // Check if a payment already exists for this invoice (duplicate guard)
      const existingPayment = await getPaymentByInvoiceId(teacherId, invoiceId)
      if (!existingPayment) {
        // Fetch the full invoice to get all fields for the payment record
        const invoiceSnap = await getDoc(invoiceRef)
        if (invoiceSnap.exists()) {
          const invoice = invoiceSnap.data() as InvoiceData
          await addPayment(teacherId, {
            studentId: invoice.studentId,
            studentName: invoice.studentName,
            amount: Number(invoice.amount),
            currency: 'USD',
            method: 'Invoice',
            paidAt: invoice.date
              ? Timestamp.fromDate(new Date(invoice.date + 'T00:00:00'))
              : Timestamp.fromDate(new Date()),
            notes: `Auto-created from invoice`,
            invoiceId,
          })
          console.log(
            '[InvoiceService] Auto-created payment for invoice:',
            invoiceId,
          )
        }
      }
    } else if (updates.status === 'Pending') {
      // Revert: soft-delete the linked payment
      const linkedPayment = await getPaymentByInvoiceId(teacherId, invoiceId)
      if (linkedPayment && linkedPayment.id) {
        await deletePayment(teacherId, linkedPayment.id)
        console.log(
          '[InvoiceService] Soft-deleted payment for reverted invoice:',
          invoiceId,
        )
      }
    }
  } catch (syncError) {
    // Log but don't fail the invoice update itself
    console.error(
      '[InvoiceService] Revenue sync error (non-blocking):',
      syncError,
    )
  }
}

/**
 * Delete an invoice from Firestore.
 * Also soft-deletes any linked payment record.
 */
export async function deleteInvoice(
  teacherId: string,
  invoiceId: string,
): Promise<void> {
  // Soft-delete linked payment first (if any)
  try {
    const linkedPayment = await getPaymentByInvoiceId(teacherId, invoiceId)
    if (linkedPayment && linkedPayment.id) {
      await deletePayment(teacherId, linkedPayment.id)
      console.log(
        '[InvoiceService] Soft-deleted payment for deleted invoice:',
        invoiceId,
      )
    }
  } catch (syncError) {
    console.error(
      '[InvoiceService] Error cleaning up linked payment (non-blocking):',
      syncError,
    )
  }

  const invoiceRef = doc(db, 'teachers', teacherId, 'invoices', invoiceId)
  await deleteDoc(invoiceRef)
}

/**
 * Send an invoice via email using the Cloud Function.
 * Updates the invoice doc with sent: true and sentAt timestamp.
 */
export async function sendInvoice(
  teacherId: string,
  invoiceId: string,
  emailData: {
    recipientEmail: string
    studentName: string
    teacherName: string
    amount: number
    date: string
    notes?: string
    billingType?: string
    billingPeriod?: string
    lessonDate?: string
  },
): Promise<void> {
  const sendInvoiceEmail = httpsCallable(functions, 'sendInvoiceEmail')

  await sendInvoiceEmail(emailData)

  // Mark invoice as sent
  const invoiceRef = doc(db, 'teachers', teacherId, 'invoices', invoiceId)
  await updateDoc(invoiceRef, {
    sent: true,
    sentAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })
}
